/**
 * Chapter 4 Optimized Layout Component
 * 
 * Compact, no-scroll reading experience specifically designed for Chapter 4.
 * Everything fits in one viewport: video/image, text, and navigation controls.
 * Uses efficient space utilization and compact navigation bar.
 * Follows the exact same pattern as Chapter1Optimized, Chapter2Optimized, and Chapter3Optimized.
 * 
 * 🎬 VIDEO INTEGRATION: Pages 1-5, 7-8 use Cloudflare Stream videos (Page 6 is cue page - no video)
 */

import { motion } from "motion/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CueInteraction } from "@/components/cue-interaction";
import { Chapter4CueChoice } from "@/components/chapter-4-cue-choice";
import { ForestJourneyMap } from "@/components/forest-journey-map";
import { Chapter4OpeningScene } from "@/components/chapter-4-opening-scene";
import { KindnessMeadowScene } from "@/components/kindness-meadow-scene";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { saveBookmark } from "@/lib/bookmark";
import { updateCurrentPosition } from "@/lib/platform-tracking";
import { 
  ArrowLeft, 
  ArrowRight, 
  Home,
  BookOpen,
  Sparkles
} from "lucide-react";

// Chapter 4 spreads data with Cloudflare Stream video IDs
const chapter4Data = {
  title: "Chapter 4: The Kindness Meadow",
  subtitle: "Kindness",
  cue: "Sparkling Petal",
  cueIcon: Sparkles,
  cueColor: "text-pink-600",
  bgColor: "bg-pink-50",
  spreads: [
    {
      spread: 0,
      section: "map",
    },
    {
      spread: 1,
      section: "story",
      videoId: "9d1201f35d4f0bf5d3826f1a122e16ad", // Cloudflare Stream video
      text: "After their brave crossing and a night of celebration at the Forest Festival, Mila and her friends noticed a glowing path on their map, sparkling in the morning sun. \"Look!\" said Mila. \"The next clue points to Kindness Meadow. I wonder who needs our help today?\""
    },
    {
      spread: 2,
      section: "story",
      videoId: "d1ddd363f4b43523a68c5468be699f37", // Cloudflare Stream video
      text: "As they entered the meadow, wildflowers swayed in the breeze and butterflies filled the air. But the friends soon spotted a group of butterflies fluttering frantically, their wings stuck together by sticky spider silk, and a young hedgehog sitting alone, looking lost and sad."
    },
    {
      spread: 3,
      section: "story",
      videoId: "3e1fc2973e8bbb4a3187c30adddab82e", // Cloudflare Stream video
      text: "Suddenly, Gentle Deer appeared behind them, her eyes gentle and wise. \"Kindness is like a ripple in a pond,\" she said. \"One small act can spread far and wide.\""
    },
    {
      spread: 4,
      section: "story",
      videoId: "46f52e900a394d9ad58c60dfa27b967f", // Cloudflare Stream video
      text: "Pip the porcupine puffed out his chest. \"I'll help the butterflies! Unless they're ticklish. If they giggle, I might turn into a giggling mouse tornado!\"\n\nBella giggled, \"Don't worry, Max. I'll hold you down if you start spinning!\""
    },
    {
      spread: 5,
      section: "story",
      videoId: "194a724cc7fe62e3bbead81e8c5b0333", // Cloudflare Stream video
      text: "Mila knelt beside the hedgehog. \"Would you like some help?\" she asked softly. The hedgehog nodded, sniffling. Mila offered a soft flower petal to comfort him while Bella and Max hurried to the butterflies."
    },
    {
      spread: 6,
      section: "cue",
      visualComponent: "KindnessMeadowScene", // Cue page - uses visual component (no video)
      cue: "Sparkling Petal",
      cueText: "Let's make someone smile! Say something kind out loud—even a tiny word counts."
    },
    {
      spread: 7,
      section: "story",
      videoId: "04d842647345004b5bcc254af0e27ef9", // Cloudflare Stream video
      text: "Soon, the butterflies were free, and the hedgehog was smiling. The butterflies danced around the friends, and the hedgehog shared his picnic snacks with everyone. Laughter and kindness filled the meadow."
    },
    {
      spread: 8,
      section: "story",
      videoId: "601191735bab3549ef957fd8274b7bd7", // Cloudflare Stream video
      text: "As the sun dipped lower, Gentle Deer handed Mila a petal with a message: \"True kindness always finds its way home.\" Mila tucked it into her bag, and the friends wondered what their next adventure would bring."
    }
  ]
};

// Visual component mapping (for cue page only)
const visualComponents = {
  Chapter4OpeningScene,
  KindnessMeadowScene
};

interface Chapter4OptimizedProps {
  onBackToContents: () => void;
}export function Chapter4Optimized({ onBackToContents }: Chapter4OptimizedProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Initialize spread from URL parameter or default to 0
  const spreadParam = searchParams.get('spread');
  const initialSpread = spreadParam ? parseInt(spreadParam) : 0;
  const [currentSpread, setCurrentSpread] = useState(initialSpread);
  const [completedCues, setCompletedCues] = useState<Set<string>>(new Set());

  const handleCueComplete = () => {
    setCompletedCues(prev => new Set(prev).add('sparkling-petal-cue'));
  };

  const totalSpreads = chapter4Data.spreads.length;
  const currentSpreadData = chapter4Data.spreads[currentSpread];
  
  // Reset spread when coming from bookmark
  useEffect(() => {
    const spreadParam = searchParams.get('spread');
    const targetSpread = spreadParam ? parseInt(spreadParam) : 0;
    setCurrentSpread(targetSpread);
  }, [searchParams]);
  
  // Save bookmark when spread changes
  useEffect(() => {
    saveBookmark(4, currentSpread, chapter4Data.title);
  }, [currentSpread]);

  // Update platform tracking when position changes
  useEffect(() => {
    updateCurrentPosition(4, currentSpread);
  }, [currentSpread]);

  // Send analytics to PostgreSQL using new API
  useEffect(() => {
    const sendAnalyticsAsync = async () => {
      try {
        const { sendAnalytics } = await import('@/lib/analytics/client');
        await sendAnalytics(4, currentSpread + 1);
        console.log(`✅ Enhanced analytics sent to PostgreSQL - Chapter 4, Spread ${currentSpread + 1}`);
      } catch (error) {
        console.warn('Failed to send enhanced analytics:', error);
      }
    };
    sendAnalyticsAsync();
  }, [currentSpread]);

  const handleNextSpread = useCallback(() => {
    if (currentSpread < totalSpreads - 1) {
      setCurrentSpread(currentSpread + 1);
    } else {
      // Go to Activities page after the last story spread
      navigate("/book/chapter/4/activity");
    }
  }, [currentSpread, totalSpreads, navigate]);

  const handlePrevSpread = useCallback(() => {
    if (currentSpread > 0) {
      setCurrentSpread(currentSpread - 1);
    } else {
      navigate("/book/contents");
    }
  }, [currentSpread, navigate]);

  // Enhanced Activities button tracking with proper useCallback
  const handleActivities = useCallback(async () => {
    try {
      const { trackButton, sendAnalytics } = await import('@/lib/analytics/client');
      
      // Track the specific navigation interaction
      trackButton('activities_button', {
        navigation_source: 'chapter_nav',
        position: { x: 0, y: 0 } // Top right nav bar
      });
      
      // Send enhanced analytics immediately
      const chapterNumber = location.pathname.includes('chapter/4') ? 4 : 
                           location.pathname.includes('chapter/5') ? 5 : 6;
      
      await sendAnalytics(chapterNumber, currentSpread + 1);
      
      console.log(`📊 Enhanced Activities button tracking:`, {
        chapter: chapterNumber,
        spread: currentSpread + 1,
        buttonType: 'activities_button',
        navigationSource: 'chapter_nav'
      });
    } catch (error) {
      console.warn('Failed to track activities button:', error);
    }
    
    // Navigate to appropriate chapter activity
    const chapterNumber = location.pathname.includes('chapter/4') ? 4 : 
                         location.pathname.includes('chapter/5') ? 5 : 6;
    navigate(`/book/chapter/${chapterNumber}/activity`);
  }, [navigate, currentSpread, location.pathname]);



  const renderVisual = (componentName?: string, imageUrl?: string) => {
    // If we have a visual component (for cue page), render it
    if (componentName) {
      const Component = visualComponents[componentName as keyof typeof visualComponents];
      if (Component) {
        return (
          <div className="h-full flex items-center justify-center overflow-hidden">
            <Component className="max-h-[120%] max-w-full object-contain object-bottom" />
          </div>
        );
      }
    }
    
    // Fallback: If we have a direct image URL (legacy support)
    if (imageUrl) {
      return (
        <div className="h-full flex items-center justify-center overflow-hidden">
          <motion.img
            src={imageUrl}
            alt="Chapter 4 illustration"
            className="max-h-full max-w-full object-contain rounded-xl shadow-sm"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-pink-50 to-rose-50">
      
      {/* COMPACT HEADER - TRANSLUCENT */}
      <div className="bg-white/30 backdrop-blur-sm border-b border-pink-200/50 px-4 py-2 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          {/* Left: Back + Chapter Title */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-forest-600 to-golden-600 rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-forest-800">Brave Things Books</div>
              </div>
            </div>
            <div className="text-forest-400">•</div>
            <Button
              onClick={onBackToContents}
              variant="ghost"
              size="sm"
              className="text-forest-700 px-2 py-1 h-8 hover:bg-transparent hover:text-forest-600"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              Contents
            </Button>
            <div className="text-forest-400">•</div>
            <h1 className="text-lg font-bold text-forest-700">
              Chapter 4: The Kindness Meadow <span className="text-sm font-normal text-forest-600">• Kindness</span>
            </h1>
          </div>
          
          {/* Right: Badge + Activities + Print */}
          <div className="flex items-center gap-2">
            <Badge 
              className="text-xs px-2 py-1 border bg-white/50 backdrop-blur-sm" 
              style={{ 
                color: '#5D4E37', 
                borderColor: '#E6D2AA' 
              }}
            >
              ✨ Sparkling Petal
            </Badge>
            
            <Button
              onClick={handleActivities}
              variant="outline"
              size="sm"
              className="text-xs px-2 py-1 h-8 border-pink-300 text-pink-700 bg-white/20 hover:bg-transparent hover:text-forest-600"
            >
              🎯 Activities
            </Button>
            

          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col overflow-hidden">
        
        {currentSpreadData?.section === "map" ? (
          /* FULL-SCREEN MAP PAGE (Spread 0) */
          <div className="fixed inset-0 z-50">
            <ForestJourneyMap currentChapter={4} />
          </div>
        ) : currentSpreadData?.section === "cue" ? (
          /* CUE PAGE - FULL PAGE LAYOUT WITH BACKGROUND IMAGE */
          <div className="flex-1 relative overflow-hidden">
            
            {/* Background Image */}
            <div className="absolute inset-0">
              {renderVisual(currentSpreadData?.visualComponent)}
              <div className="absolute inset-0 bg-gradient-to-b from-pink-400/40 via-pink-300/60 to-pink-500/70"></div>
            </div>

            {/* Main Cue Box - Centered */}
            <div className="relative z-10 h-full flex items-center justify-center p-8">
              <motion.div 
                className="bg-cream-50 border-4 border-pink-400 rounded-3xl p-12 text-center mx-auto max-w-2xl"
                style={{ 
                  backgroundColor: '#FDF6E3',
                  borderColor: '#EC4899'
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                {/* Celebratory Emojis at Top */}
                <div className="text-5xl mb-8">
                  ✨ 🌸 ✨
                </div>
                
                {/* Magic Cue Found Title */}
                <h2 className="text-4xl font-bold mb-10" style={{ color: '#DB2777' }}>
                  ✨ Magic Cue Found! ✨
                </h2>
                
                {/* Instruction Text */}
                <div className="text-3xl leading-relaxed font-medium mb-6" style={{ color: '#EC4899' }}>
                  Click the glowing Sparkling Petal in the corner to try the activity!
                </div>
              </motion.div>
            </div>

            {/* Cue Interaction */}
            <div className="absolute top-8 right-8 z-20">
              <Chapter4CueChoice
                onComplete={handleCueComplete}
                isCompleted={completedCues.has('sparkling-petal-cue')}
              />
            </div>
          </div>
        ) : (
          /* STORY PAGES - SPLIT LAYOUT WITH VIDEO */
          <>
            {/* TOP: VIDEO SECTION - 70% of screen height */}
            <div className="h-[70%] bg-transparent overflow-hidden relative">
              <div className="w-full h-full rounded-lg overflow-hidden bg-gradient-to-br max-w-5xl mx-auto">
                {currentSpreadData?.videoId ? (
                  /* CLOUDFLARE STREAM VIDEO */
                  <iframe
                    key={currentSpreadData.videoId} // Force re-mount on video change
                    src={`https://iframe.videodelivery.net/${currentSpreadData.videoId}?autoplay=true&muted=true&controls=false&preload=auto&fit=crop`}
                    style={{ 
                      border: 'none', 
                      width: '115%',  // 115% zoom (removes black bars for Chapter 4)
                      height: '115%', // 115% zoom
                      position: 'relative',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none'
                    }}
                    allow="autoplay; encrypted-media"
                    title={`Chapter 4 Spread ${currentSpread + 1} Video`}
                    onLoad={() => {
                      console.log(`✅ Chapter 4 Cloudflare Stream iframe loaded - Spread ${currentSpread + 1}`);
                    }}
                  />
                ) : (
                  /* FALLBACK: Visual component or image */
                  renderVisual(currentSpreadData?.visualComponent)
                )}
              </div>
            </div>
            
            {/* BOTTOM: TEXT SECTION - 30% of screen height */}
            <div className="h-[30%] flex flex-col px-8 py-6">
              <div className="flex-1 flex items-center">
                <div className="text-xl leading-relaxed text-gray-900 max-w-5xl mx-auto" style={{ textAlign: 'left' }}>
                  {currentSpreadData?.text?.split('\n\n').map((paragraph, index) => (
                    <p key={index} className={index > 0 ? 'mt-4' : ''} style={{ textAlign: 'left' }}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Minimal Navigation Controls */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex justify-between items-center py-0 px-2">
          {/* Previous Button - Ultra Minimal */}
          <Button
            onClick={handlePrevSpread}
            disabled={currentSpread === 0}
            variant="ghost"
            size="sm"
            className="text-forest-600 disabled:opacity-50 px-2 py-0 h-6 hover:bg-transparent hover:text-forest-600"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Prev
          </Button>

          {/* Chapter Progress with Dots */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-forest-600">Chapter Progress</span>
            <div className="flex gap-1">
              {chapter4Data.spreads.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSpread(index)}
                  className={`w-2 h-2 rounded-full  ${
                    index === currentSpread
                      ? 'bg-forest-500'
                      : 'bg-forest-200'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-forest-600">{currentSpread} of {chapter4Data.spreads.length - 1}</span>
          </div>

          {/* Next Button - Ultra Minimal */}
          <Button
            onClick={handleNextSpread}
            variant="ghost"
            size="sm"
            className="text-forest-600 px-2 py-0 h-6 hover:bg-transparent hover:text-forest-600"
          >
            {currentSpread === totalSpreads - 1 ? "Activities" : "Next"}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>


    </div>
  );
}
